<template>
  <div class="modal" @click.self="close">
    <button @click="closeModal()" class="modal__Overlay"></button>
    <success-prompt :updated="updated" :message="prompt.success"></success-prompt>

    <div class="modal__Inner">
      <section class="modal__Post">
        <header class="modal__Header">
          <h1 class="modal__Title">{{ $store.getters.getStringTranslation('addTag') }}</h1>

          <div class="modal__Meta">
            <ul>
              <li class="modal__MetaItem--Button">
                <button
                  v-if="prompt.save.length > 0"
                  @click="update('tag'), closeModal()"
                  :disabled="updated === true"
                  class="button--Small"
                >
                  {{ prompt.save }}
                </button>
              </li>
              <li class="modal__MetaItem">
                <button @click="closeModal()" class="modal__MetaLink">
                  <icon name="times"></icon>
                </button>
              </li>
            </ul>
          </div>
        </header>

        <div class="modal__Scrollable" id="editorModal">
          <error-prompt class="error--Modal" :errors="errors"></error-prompt>
          <loader class="imageLoader" :loading="loading || updating" />
          <template v-if="loading === false">
            <div class="cardPreview">
              <div class="cardPreview__Columns">
                <div class="cardPreview__Column" v-if="item.status !== undefined">
                  <h2 class="cardPreview__Title">{{ $store.getters.getStringTranslation('status') }}</h2>
                  <p class="cardPreview__Status">{{ capitalize(item.status) }}</p>
                </div>
                <div class="cardPreview__Column">
                  <h2 class="cardPreview__Title">{{ $store.getters.getStringTranslation('languages') }}</h2>
                  <ul class="cardPreview__Meta">
                    <li v-for="translation in langs" :key="translation.id">
                      <!-- Regular link for exising translation -->
                      <template v-if="getTranslationObject(item.l10n, translation.toLowerCase()) !== undefined">
                        <router-link class="cardPreview__MetaLink" :to="{ query: { lang: translation.toLowerCase() } }"
                          >{{ translation }}
                          <icon
                            name="check"
                            v-if="getTranslationObject(item.l10n, translation.toLowerCase()).name.length > 0"
                          ></icon>
                        </router-link>
                        <button
                          v-if="item.l10n !== undefined && item.l10n.length > 1"
                          class="cardPreview__MetaPopup"
                          @click="removeTranslation(translation)"
                        >
                          <icon name="times"></icon> Remove
                        </button>
                      </template>
                      <!-- New translation -->
                      <button class="cardPreview__MetaLink--Add" @click="addTranslation(translation)" v-else>
                        {{ translation }} <icon name="plus" weight="solid"></icon>
                      </button>
                    </li>
                  </ul>
                </div>
                <div class="cardPreview__Column" v-if="$route.params.id !== undefined">
                  <h2 class="cardPreview__Title">{{ $store.getters.getStringTranslation('controls') }}</h2>
                  <ul class="cardPreview__Meta">
                    <li>
                      <button class="cardPreview__MetaButton" @click="disable('tag')">
                        <icon v-if="item.status === 'published'" name="eye-slash"></icon>
                        <icon v-else name="eye"></icon>
                        {{ disableLabel }}
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div class="formSection">
              <div class="formSection__Row--Gray">
                <label class="formLabel">Tag type</label>
                <select class="formSelect" v-model="item.type">
                  <option v-for="tab in types" :key="tab.id" :value="tab.toLowerCase()">
                    {{ $store.getters.getStringTranslation(tab) }}
                  </option>
                </select>
              </div>
              <div class="formSection__Row">
                <p>
                  <label class="formLabel" for="editTitle">{{ $store.getters.getStringTranslation('title') }}</label>
                  <input
                    required
                    v-model="getTranslationObject(item.l10n).name"
                    type="text"
                    class="formInput"
                    id="editTitle"
                  />
                </p>
                <p>
                  <label class="formLabel" for="editAbbreviation">{{
                    $store.getters.getStringTranslation('abbreviation')
                  }}</label>
                  <input
                    required
                    v-model="getTranslationObject(item.l10n).abbreviation"
                    type="text"
                    class="formInput"
                    id="editAbbreviation"
                  />
                </p>
                <p>
                  <label class="formLabel" for="editSummary">{{
                    $store.getters.getStringTranslation('summary')
                  }}</label>
                  <textarea
                    id="editSummary"
                    class="formInput--Textarea"
                    v-model="getTranslationObject(item.l10n).summary"
                  ></textarea>
                </p>
                <p>
                  <search-tag
                    :types="item.type === 'country' ? ['region'] : [item.type]"
                    :lang="lang"
                    :tags="item.parentTag"
                    v-model="item.parent_id"
                    :placeholder="
                      item.type === 'country'
                        ? 'Choose region & sub-region'
                        : $store.getters.getStringTranslation('parentTag')
                    "
                    :errorMessage="$store.getters.getStringTranslation('noTagsFound')"
                    :label="
                      item.type === 'country'
                        ? 'Choose region & sub-region'
                        : $store.getters.getStringTranslation('parentTag')
                    "
                    :showLabel="true"
                    :limit="false"
                    :multiple="false"
                  />
                </p>
              </div>
              <div class="formSection__Row" v-if="['organisation'].includes(item.type)">
                <p>
                  <label class="formLabel" for="editWebsite">Website</label>
                  <input
                    v-model="getTranslationObject(item.l10n).external_url"
                    type="text"
                    class="formInput"
                    id="editWebsite"
                  />
                </p>
                <search-tag
                  :types="['country']"
                  :lang="lang"
                  :tags="item.tag_ids"
                  v-model="item.tags"
                  :placeholder="$store.getters.getStringTranslation('selectCountry')"
                  :errorMessage="$store.getters.getStringTranslation('noCountries')"
                  :showLabel="false"
                  :limit="false"
                />
                <p>
                  <image-upload
                    :required="false"
                    v-model="getTranslationObject(item.l10n).thumbnail_urls"
                  ></image-upload>
                </p>
              </div>
              <div class="formSection__Row" v-if="['country', 'region'].includes(item.type)">
                <p>
                  <label class="formLabel" for="editLocation">{{
                    $store.getters.getStringTranslation('location')
                  }}</label>
                  <gmap-autocomplete
                    class="formInput"
                    :value="getTranslationObject(item.l10n).location_info"
                    @place_changed="setPlace"
                  ></gmap-autocomplete>
                </p>
              </div>
            </div>
          </template>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { ISOlanguages } from '@/router/globals.js'
import HTTP from '@/http-common.js'

import GetTranslationObject from '@/components/mixins/GetTranslationObject.js'
import GetProperty from '@/components/mixins/GetProperty.js'
import ReduceItemData from '@/components/mixins/ReduceItemData.js'
import DisableItem from '@/components/mixins/DisableItem.js'
import ManageTranslations from '@/components/mixins/ManageTranslations.js'
import Capitalize from '@/components/mixins/Capitalize.js'
import UpdatePost from '@/components/mixins/UpdatePost.js'

import SearchTag from '@/components/editor/blocks/SearchTag.vue'
import ImageUpload from '@/components/editor/ImageUpload.vue'
import Loader from '@/components/partials/Loader.vue'
import ErrorPrompt from '@/components/editor/ErrorPrompt.vue'
import SuccessPrompt from '@/components/editor/SuccessPrompt.vue'

export default {
  name: 'EditTag',
  components: {
    SearchTag,
    ImageUpload,
    Loader,
    ErrorPrompt,
    SuccessPrompt,
  },
  props: ['propLanguage', 'queryParams', 'types'],
  mixins: [GetProperty, GetTranslationObject, ReduceItemData, DisableItem, ManageTranslations, Capitalize, UpdatePost],
  data() {
    return {
      item: {
        l10n: [
          {
            lang: this.propLanguage,
            name: '',
            abbreviation: '',
            summary: '',
            location_info: '',
          },
        ],
        type: this.types[0],
        contents: {
          external_url: '',
          thumbnail_url: '',
          latitude: '',
          longitude: '',
        },
        tag_ids: [],
      },
      status: 'published',
      country: [],
      prompt: {
        save: 'Save',
        success: '',
      },
      parent: null,
      loading: true,
      updating: false,
      updated: false,
      errors: '',
    }
  },
  computed: {
    lang() {
      if (this.$route.query.lang !== undefined) {
        return this.$route.query.lang
        //  If not, retrieve language from prop
      } else {
        return this.propLanguage
      }
    },
    langs() {
      return ISOlanguages
    },
    countries() {
      return this.$store.state.tags.countries.tags
    },
    getActiveParentTags() {
      if (this.item.parent_id) {
        return [this.item.parent_id]
      } else {
        return []
      }
    },
  },
  created() {
    if (this.$route.params.id !== undefined) {
      HTTP.get(`tag/${this.$route.params.id}`)
        .then((response) => {
          this.item = response.data
          this.loading = false

          this.prompt.save = 'Save'
          this.prompt.success = 'Success! Tag updated.'
        })
        .catch((e) => {
          this.errors = e.response ? e.response.data : console.error(e)
          this.loading = false
        })
    } else {
      this.loading = false
      this.$router.push({ query: { lang: this.lang } })

      this.prompt.save = this.$store.getters.getStringTranslation('create')
      this.prompt.success = this.$store.getters.getStringTranslation('tagSuccess')
    }

    // Define parent URL
    this.parent = {
      path: this.$route.matched.find((x) => x.parent !== undefined).parent.path,
      query: {
        search: JSON.stringify(this.queryParams),
      },
    }
  },
  methods: {
    syncTags() {
      // Add tag values to tag
      // this.item.tag_ids = this.country.map(function (a) { return a.value })
    },
    closeModal() {
      if (this.changed === true) {
        if (confirm('Are you sure you wish to close the editor? Everything not saved will be lost!')) {
          this.$router.push(this.parent)
        }
      } else {
        this.$router.push(this.parent)
      }
    },
    setPlace(place) {
      this.getTranslationObject(this.item.l10n).location_info = place.formatted_address
      this.item.contents.location = {
        latitude: place.geometry.location.lat(),
        longitude: place.geometry.location.lng(),
      }
    },
  },
}
</script>
